//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import angular, { IScope } from 'angular';
import { INgRedux } from 'ng-redux';

import { CollectionStatusType } from '@apis/collections/CollectionStatusType';
import { KEYCODES, KEYS } from '@constants/keyboard';
import { galleriesAddToGalleriesModalActions } from '@galleries/actions/galleriesAddToGalleriesModalActions';
import { ICuralateStore } from '@models/AppState';
import * as Permissions from '@services/permissions';
import '@streams-drawer/actions/streams-drawer-actions';
import { IStreamsDrawerState } from '@streams-drawer/model/AppState';
import { IStream } from '@streams-drawer/model/IStream';
import getHasUserDefinedStreams from '@streams-drawer/selectors/getHasUserDefinedStreams';
import {
  nameToStreamType,
  StreamModalType,
  StreamsDrawerStreamType,
} from '@streams-drawer/streams-drawer-constants';
import '@streams-drawer/streams-drawer/streams-drawer';
import { actions as reactStreamDrawerActions } from '@streams/actions/streamDrawerActions';
import { IBulkActionsState } from 'app/bulk-actions/model/AppState';
import { BulkActionNames } from 'app/bulk-actions/model/BulkActionNames';
import { ISelectedItem } from 'app/bulk-actions/model/ISelectedItem';
import { getShouldNotAllowUgcStreams } from '@streams/selectors/getShouldNotAllowUgcStreams';
import { SearchFilterService } from '@legacy-components/filters/search-filter-service';
import { SEARCH_FILTERS as searchFiltersConst } from '@search-filters/constants/searchFilters';

import template from './streams-drawer-container.html';
import { getIsUiaVideoClosedCaptionsEnabled } from '@legacy-components/common/universal-image-actions/selectors/getIsUiaVideoClosedCaptionsEnabled';

export class StreamsDrawerContainer {
  private static mapStateToThis = (state: ICuralateStore) => {
    const streamsDrawerState = state.streams
      .streamsDrawer as IStreamsDrawerState;
    const universalImageActionsState = state.bulkActions as IBulkActionsState;

    const isUIAModalOpen = state.uiaModal.modalState.isUIAModalOpen;

    const isBazaarvoiceSyndicationEnabled =
      state.session.features.bazaarvoice_network_syndication_frontend;
    const isVideoCaptionsFeatureEnabled =
      getIsUiaVideoClosedCaptionsEnabled(state);
    const isLegacyShareV2Vendor =
      state.session.client.isShareV2Vendor && !isBazaarvoiceSyndicationEnabled;
    // TODO: Add a feature flag check here
    const isModerationServicesPublisherClient = false;
    // Moderation services client (signified by the new isModerationServices flag on the client_relationship)
    const isModerationServices = isLegacyShareV2Vendor
      ? state.share.data.publisherClients[0].isModerationServices
      : undefined; // undefined, for one-time binding support

    return {
      brandInstagramUsername: streamsDrawerState.brandInstagramUsername,
      assetFeedSourceConnectionStates:
        state.streams.assetFeedsModal.sourceConnectionStates,
      clientName: state.session.client.name,
      contentView: streamsDrawerState.contentView,
      filters: streamsDrawerState.filters,
      lastSavedFilters: streamsDrawerState.lastSavedFilters,
      hasUserDefinedStream: getHasUserDefinedStreams(state),
      isAssetFeedsOnboarding: streamsDrawerState.isAssetFeedsOnboarding,
      isAssetFeedStreamUpdatingSources:
        state.streams.assetFeedsModal.isUpdatingSources,
      isAssetFeedStreamUpdatingFilters:
        state.streams.assetFeedsModal.isUpdatingFilters,
      isContentDiscoveryEnabled: streamsDrawerState.isContentDiscoveryEnabled,
      isEndOfContentReached: streamsDrawerState.isEndOfContentReached,
      isFetchingBrandInstagramUsername:
        streamsDrawerState.isFetchingBrandInstagramUsername,
      isFetchingFilteredItems: streamsDrawerState.isFetchingFilteredItems,
      isFetchingFirstRunStatus: streamsDrawerState.isFetchingFirstRunStatus,
      isFetchingItems: streamsDrawerState.isFetchingItems,
      isFetchingNextItems: streamsDrawerState.isFetchingNextItems,
      isRefreshingStreams: streamsDrawerState.isRefreshingStreams,
      isFiltersSidebarOpen: streamsDrawerState.isFiltersSidebarOpen,
      isModerationServices,
      isModerationServicesPublisherClient,
      isRemovalConfirmationModalOpen:
        streamsDrawerState.isRemovalConfirmationModalOpen,
      isLegacyShareV2Vendor: isLegacyShareV2Vendor,
      isVideoCaptionsFeatureEnabled,
      isStandardStreamModalOpen:
        streamsDrawerState.openStreamModal === StreamModalType.STANDARD,
      isDeleteStreamModalOpen: streamsDrawerState.isDeleteStreamModalOpen,
      isAutoApprovalEntryModalOpen:
        streamsDrawerState.isAutoApprovalEntryModalOpen,
      isAutoApprovalModalOpen: streamsDrawerState.isAutoApprovalModalOpen,
      isAssetFeedsStreamModalOpen:
        streamsDrawerState.openStreamModal === StreamModalType.ASSET_FEEDS,
      isStreamsDrawerOpen: streamsDrawerState.isStreamsDrawerOpen,
      isUIAModalOpen: state.uiaModal.modalState.isUIAModalOpen,
      isAddLabelsModalOpen:
        state.bulkActions.confirmationModal === BulkActionNames.ADD_LABELS,
      items: streamsDrawerState.items,
      moderationStatus: streamsDrawerState.moderationStatus,
      mentionsMyBrand: streamsDrawerState.mentionsMyBrand,
      openStreamModal: streamsDrawerState.openStreamModal,
      clientRejectionReasons: streamsDrawerState.clientRejectionReasons,
      selectedItems: universalImageActionsState.selectedItems,
      isVideoActionInProgress:
        universalImageActionsState.isVideoActionInProgress,
      selectedStream: streamsDrawerState.selectedStream,
      creatorClients: streamsDrawerState.creatorClients,
      selectedStreamType: streamsDrawerState.selectedStreamType,
      shouldNotAllowUgcStreams: getShouldNotAllowUgcStreams(state),
      showContentDiscoveryFirstRun:
        streamsDrawerState.showContentDiscoveryFirstRun,
      sort: streamsDrawerState.sort,
      streams: streamsDrawerState.streams,
      summaryItemSets: streamsDrawerState.summaryItemSets,
      recommendationsStreams: streamsDrawerState.recommendationsStreams,
      totalItemCount: streamsDrawerState.totalItemCount,
      taggedByCuralatePendingCount:
        streamsDrawerState.taggedByCuralatePendingCount,
      taggedByBvAutomationConfiguration:
        streamsDrawerState.taggedByBvAutomationConfiguration,
      taggedByBvAutomationActiveConfiguration:
        streamsDrawerState.taggedByBvAutomationActiveConfiguration,
      connectPendingCount: streamsDrawerState.connectPendingCount,
      trackingTimeout: streamsDrawerState.trackingTimeout,
      galleryselectors: streamsDrawerState.gallerySelection,
    };
  };

  public filterCount;
  public isLegacyShareV2Vendor;
  public isAssetFeedsDrawer;
  public isUIAModalOpen;
  public isAddLabelsModalOpen;
  public isVideoCaptionsFeatureEnabled;

  private openAddToGalleriesModal: (items: ISelectedItem[]) => void;
  private addSelectedMediaToShare: () => void;
  private checkContentDiscoveryFlag: () => void;
  private fetchAssetFeedsStream: () => Promise<void>;
  private fetchBrandInstagram: () => void;
  private fetchBrandFacebook: () => void;
  private fetchContentItems: (stream: IStream, filters: any) => void;
  private fetchNextContentItems: (stream: IStream, filters: any) => void;
  private getContentDiscoveryFirstRunStatus: () => void;
  private isAssetFeedsOnboarding: boolean;
  private isAssetFeedStreamUpdatingSources: boolean;
  private isAssetFeedStreamUpdatingFilters: boolean;
  private isStreamsDrawerOpen: boolean;
  private refreshStreams: () => Promise<void>;
  private removeSelectedMediaFromStream: () => void;
  private streams: IStream[];
  private selectStream: (Stream: IStream, forceReload?: boolean) => void;
  private unsubscribe: () => void;
  private filters: any;
  private lastSavedFilters: any;
  private hasUnsavedAssetFeedFilters: boolean;
  private selectedStream: IStream;
  private updateFilters;
  private updateSort;
  private updateModerationStatus;
  private updateMentionsMyBrand;
  private updateConnectVendor;
  private recommendationsStreams: IStream[];
  private selectedItems: ISelectedItem[];
  private isVideoActionInProgress: boolean;
  private isRecommendationStream: boolean;
  private isTaggedByCuralateStream: boolean;
  private isConnectStream: boolean;
  private moderationStatus: CollectionStatusType;
  private openStreamModal: StreamModalType;
  private closeStreamModal: () => void;
  private closeStreamsDrawer: () => void;
  private closeConfirmationModal: () => void; // close add labels modal
  private clearSelectedItems: () => void;

  private ESC_KEYMAP: number[];

  public constructor(
    $ngRedux: INgRedux,
    private $scope: IScope,
    private searchFilterService: SearchFilterService,
    StreamsDrawerActions,
    BulkActionsActions,
    private SEARCH_FILTERS: typeof searchFiltersConst,
  ) {
    const actions = {
      openAddToGalleriesModal: galleriesAddToGalleriesModalActions.openModal,
      ...reactStreamDrawerActions,
      ...StreamsDrawerActions,
      ...BulkActionsActions,
    };

    this.unsubscribe = $ngRedux.connect(
      StreamsDrawerContainer.mapStateToThis,
      actions,
    )(this);
  }

  public addItems() {
    if (this.isLegacyShareV2Vendor) {
      this.addSelectedMediaToShare();
    } else {
      this.openAddToGalleriesModal(this.selectedItems);
    }
  }

  public removeItems() {
    if (Permissions.hasPermissionForTask('library:manage_collections')) {
      this.removeSelectedMediaFromStream();
    }
  }

  public endOfContentReached() {
    this.fetchNextContentItems(this.selectedStream, this.filters);
  }

  public checkRecommendationStream() {
    return this.recommendationsStreams
      .map((s) => s.id)
      .includes(this.selectedStream.id);
  }

  public checkIsTaggedByCuralateStream() {
    return (
      nameToStreamType(this.selectedStream) ===
      StreamsDrawerStreamType.TAGGED_BY_CURALTE
    );
  }

  public checkIsConnectStream() {
    return (
      nameToStreamType(this.selectedStream) === StreamsDrawerStreamType.CONNECT
    );
  }

  public handleEscapeKeyPress() {
    if (!this.isUIAModalOpen) {
      if (this.openStreamModal) {
        this.closeStreamModal();
      } else if (this.isAddLabelsModalOpen) {
        this.closeConfirmationModal();
      } else {
        this.clearSelectedItems();
        this.closeStreamsDrawer();
      }
    }
  }

  public $onInit() {
    (this.ESC_KEYMAP = [KEYCODES[KEYS.ESC]]), this.checkContentDiscoveryFlag();
    this.getContentDiscoveryFirstRunStatus();
    this.fetchBrandInstagram();
    // Uncomment if we add facebook as a gaf source this.fetchBrandFacebook();

    if (this.isAssetFeedsDrawer) {
      this.fetchAssetFeedsStream();
    } else {
      this.refreshStreams();
    }

    if (this.selectedStream) {
      this.isRecommendationStream = this.checkRecommendationStream();
      this.isTaggedByCuralateStream = this.checkIsTaggedByCuralateStream();
      this.isConnectStream = this.checkIsConnectStream();
    }

    this.searchFilterService
      .getObservableForFilterSet(
        this.SEARCH_FILTERS.COLLECTION_CONTENT_FILTERS_QUICK_SEARCH,
      )
      .subscribe((searchFilterSet) => {
        const filters = searchFilterSet.setObject;
        this.updateFilters(filters);
      });

    this.searchFilterService
      .getObservableForFilterSet(this.SEARCH_FILTERS.COLLECTION_CONTENT_FILTERS)
      .subscribe((searchFilterSet) => {
        const filters = searchFilterSet.setObject;
        this.updateFilters(filters);

        this.filterCount = searchFilterSet.filterCount;

        this.hasUnsavedAssetFeedFilters =
          this.isAssetFeedsDrawer &&
          !angular.equals(this.filters, this.lastSavedFilters);
      });

    this.searchFilterService
      .getObservableForFilterSet(this.SEARCH_FILTERS.STREAMS_FILTERS_SORT)
      .subscribe((searchFilterSet) => {
        const sort = searchFilterSet.setObject.orderBy[0];
        this.updateSort(sort);
      });

    this.searchFilterService
      .getObservableForFilterSet(
        this.SEARCH_FILTERS.STREAM_FILTERS_MODERATION_STATUS,
      )
      .subscribe((searchFilterSet) => {
        const status = searchFilterSet.setObject?.moderationStatus?.[0];
        this.updateModerationStatus(status);
      });

    this.searchFilterService
      .getObservableForFilterSet(
        this.SEARCH_FILTERS.STREAM_FILTERS_MENTIONS_STATUS,
      )
      .subscribe((searchFilterSet) => {
        const mentionsMyBrand = searchFilterSet.setObject?.mentionsMyBrand?.[0];
        this.updateMentionsMyBrand(mentionsMyBrand);
      });

    this.searchFilterService
      .getObservableForFilterSet(
        this.SEARCH_FILTERS.STREAM_FILTERS_CONNECT_VENDOR,
      )
      .subscribe((searchFilterSet) => {
        const connectVendor = searchFilterSet.setObject?.connectVendor?.[0];
        this.updateConnectVendor(connectVendor);
      });

    this.$scope.$watch(
      () => this.selectedStream,
      (newVal: IStream, oldVal: IStream) => {
        if (newVal && newVal !== oldVal) {
          this.isRecommendationStream = this.checkRecommendationStream();
          this.isTaggedByCuralateStream = this.checkIsTaggedByCuralateStream();
          this.isConnectStream = this.checkIsConnectStream();
        }
      },
    );

    this.$scope.$watch(
      () => this.isStreamsDrawerOpen,
      (newVal: boolean, oldVal: boolean) => {
        if (newVal !== oldVal && newVal === false) {
          this.searchFilterService.setFilter(
            this.SEARCH_FILTERS.COLLECTION_CONTENT_FILTERS_QUICK_SEARCH,
            'searchText',
            [null],
          );
        }
      },
    );

    // Select google asset feed stream after it is created during onboarding
    this.$scope.$watch(
      () => this.streams,
      (newVal: IStream[], oldVal: IStream[]) => {
        if (this.isAssetFeedsOnboarding && newVal !== oldVal) {
          this.selectStream(this.streams[0]);
        }
      },
    );

    // Recheck for filter changes after asset feed filters are updated
    this.$scope.$watch(
      () => this.isAssetFeedStreamUpdatingFilters,
      (newVal: boolean, oldVal: boolean) => {
        if (newVal === false && oldVal === true) {
          this.hasUnsavedAssetFeedFilters = !angular.equals(
            this.filters,
            this.lastSavedFilters,
          );
        }
      },
    );
  }

  public $onDestroy() {
    this.searchFilterService.unregisterFilterSet(
      this.SEARCH_FILTERS.STREAM_SOURCES,
    );
    this.unsubscribe();
  }
}

angular.module('curalate').component('streamsDrawerContainer', {
  bindings: {
    isAssetFeedsDrawer: '<?',
    shouldShowGalleryText: '<',
    shouldShowConnectText: '<',
  },
  controller: [
    '$ngRedux',
    '$scope',
    'SearchFilterService',
    'StreamsDrawerActions',
    'BulkActionsActions',
    'SEARCH_FILTERS',
    StreamsDrawerContainer,
  ],
  template,
});
